// Ian - 2026-09-28 - Alpine Style Finder: the quiz logic behind sections/ian-alpine-finder.liquid.
// (Re-sent 28 Sep 2026: the first push of this version never reached the draft.)
//
// The section renders question 1 as plain links and emits two JSON blobs: the collection's
// storefront filters plus a 50-product sample (payload) and the block settings (config). This
// script takes over the tile area and runs the quiz one screen at a time, Typeform-style: shape,
// colour group, optional exact colour, optional length, result.
//
// Counts are live. After every answer the section is re-requested through the Section Rendering
// API against the filtered collection URL, and the payload's facet counts on that render are
// relative to the filtered set. Any option with nothing behind it is disabled, so there is no
// route to an empty page. Repeated values (Cardigan OR Coat) go in one request: measured on the
// draft 28 Sep 2026, the facet counts and products_count come back correct for them. The
// "expand" setting can force one-request-per-value summing if that ever stops being true.
//
// Images are live too. The product sample on a filtered render is scoped to that filter, so a
// tile's picture is the first product that answer would lead to (a neutral cardigan, once
// cardigans are chosen), fetched on idle after the screen paints and cached for the page view.
// A tile with an image picked in the customiser skips the fetch. The result screen shows the
// first few products of the edit itself.
//
// Every request is cached in memory for the page view. Nothing is stored between visits: the
// collection moves under us (stock condensing), so stale counts would be worse than a round trip.
(function () {
  'use strict';

  var FALLBACK_SWATCH = {
    beige: '#d8cbb4', blue: '#51739c', bronze: '#8c6239', brown: '#7a5c43', gray: '#a9a7a3',
    grey: '#a9a7a3', green: '#6d715b', navy: '#2b3550', orange: '#be2f0f', pink: '#c98ba4',
    purple: '#7a4b7d', red: '#a3232a', yellow: '#d8c26a', black: '#2a2a2a', white: '#f4f4f4'
  };

  var schedule = window.requestIdleCallback
    ? function (fn) { window.requestIdleCallback(fn, { timeout: 1500 }); }
    : function (fn) { setTimeout(fn, 60); };

  function splitList(text) {
    return (text || '').split(',').map(function (t) { return t.trim(); }).filter(Boolean);
  }

  // "Short: Cropped, Medium: Classic" -> { short: 'Cropped', medium: 'Classic' }
  function parsePairs(text) {
    var out = {};
    splitList(text).forEach(function (pair) {
      var cut = pair.indexOf(':');
      if (cut < 1) return;
      out[pair.slice(0, cut).trim().toLowerCase()] = pair.slice(cut + 1).trim();
    });
    return out;
  }

  function readJson(el) {
    if (!el) return null;
    try { return JSON.parse(el.textContent); } catch (e) { return null; }
  }

  function findFilter(payload, wanted) {
    if (!payload || !payload.filters) return null;
    var w = (wanted || '').toLowerCase();
    if (!w) return null;
    for (var i = 0; i < payload.filters.length; i++) {
      var f = payload.filters[i];
      var param = (f.param || '').toLowerCase();
      if (param === w) return f;
      if ((f.label || '').toLowerCase() === w) return f;
      // Ian - match the tail of the parameter too, so "top-length-type" resolves if the
      // storefront label is renamed in Search & Discovery.
      if (param.slice(-w.length - 1) === '.' + w) return f;
    }
    return null;
  }

  function findValue(filter, label) {
    if (!filter) return null;
    var l = (label || '').toLowerCase();
    for (var i = 0; i < filter.values.length; i++) {
      if ((filter.values[i].label || '').toLowerCase() === l) return filter.values[i];
    }
    return null;
  }

  function firstImage(payload) {
    var list = (payload && payload.products) || [];
    for (var i = 0; i < list.length; i++) {
      if (list[i] && list[i].image) return list[i].image;
    }
    return null;
  }

  function init(root) {
    if (!root || root.hasAttribute('data-finder-ready')) return;
    root.setAttribute('data-finder-ready', '');

    var sectionId = root.getAttribute('data-ian-finder');
    var baseUrl = root.getAttribute('data-finder-url') || window.location.pathname;
    var renderId = root.getAttribute('data-finder-render-id') || 'ian-alpine-finder';
    var config = readJson(root.querySelector('[data-ian-finder-config]'));
    var payload = readJson(document.querySelector('[data-ian-finder-payload="' + sectionId + '"]'));
    if (!config) return;

    var text = config.text || {};
    var noun = config.noun || 'colours';
    var expand = splitList(config.expand).map(function (p) { return p.toLowerCase(); });
    var shapeLabels = parsePairs(config.shapeLabels);
    var resultProducts = typeof config.resultProducts === 'number' ? config.resultProducts : 4;

    var el = {
      tiles: root.querySelector('[data-finder-tiles]'),
      qnum: root.querySelector('[data-finder-qnum]'),
      question: root.querySelector('[data-finder-question]'),
      hint: root.querySelector('[data-finder-hint]'),
      crumbs: root.querySelector('[data-finder-crumbs]'),
      bar: root.querySelector('[data-finder-bar]'),
      back: root.querySelector('[data-finder-back]'),
      skip: root.querySelector('[data-finder-skip]'),
      restart: root.querySelector('[data-finder-restart]'),
      status: root.querySelector('[data-finder-status]'),
      steps: root.querySelectorAll('[data-finder-steps] > li'),
      progress: root.querySelector('[data-finder-progress]')
    };
    if (!el.tiles) return;

    // Ian - state is the answers only; the screen is derived from them.
    var state = { step: 'type', type: null, group: null, colour: null, shape: null };
    var cache = {};
    var requestToken = 0;
    var screenToken = 0;

    function pluralise(n) {
      var word = noun;
      if (n === 1) word = word.replace(/s$/, '');
      return n + ' ' + word;
    }

    // ---- selection -> filter parameters ------------------------------------------------------

    function typeFilter(p) { return findFilter(p || payload, 'filter.p.product_type'); }
    function colourFilter(p) { return findFilter(p || payload, config.colourParam || 'filter.v.t.shopify.color-pattern'); }
    function shapeFilter(p) { return config.shapeFilter ? findFilter(p || payload, config.shapeFilter) : null; }

    function typeParams(t) {
      var out = [];
      var tf = typeFilter();
      splitList(t.types).forEach(function (name) {
        var v = findValue(tf, name);
        if (v) out.push({ param: tf.param, value: v.value });
      });
      return out;
    }

    function colourParams(labels) {
      var out = [];
      var cf = colourFilter();
      labels.forEach(function (c) {
        var v = findValue(cf, c);
        if (v) out.push({ param: cf.param, value: v.value });
      });
      return out;
    }

    // Returns [{param, value}] for the current answers, using the payload's own values so no gid
    // is ever typed here.
    function params(upTo) {
      var out = [];
      if (state.type) out = out.concat(typeParams(state.type));
      if (upTo === 'type') return out;
      if (state.colour) out = out.concat(colourParams([state.colour]));
      else if (state.group) out = out.concat(colourParams(splitList(state.group.colours)));
      if (upTo === 'colour') return out;
      if (state.shape) {
        var sf = shapeFilter();
        var sv = findValue(sf, state.shape);
        if (sv) out.push({ param: sf.param, value: sv.value });
      }
      return out;
    }

    function query(list) {
      return list.map(function (p) {
        return encodeURIComponent(p.param) + '=' + encodeURIComponent(p.value);
      }).join('&');
    }

    function resultUrl() {
      var q = query(params());
      return baseUrl + (q ? '?' + q : '');
    }

    // ---- fetching ----------------------------------------------------------------------------

    function fetchPayload(list) {
      var q = query(list);
      var key = q || '__base';
      if (cache[key]) return cache[key];
      var url = baseUrl + '?' + (q ? q + '&' : '') + 'section_id=' + encodeURIComponent(renderId);
      cache[key] = fetch(url, { credentials: 'same-origin' })
        .then(function (res) {
          if (!res.ok) throw new Error('HTTP ' + res.status);
          return res.text();
        })
        .then(function (html) {
          var doc = new DOMParser().parseFromString(html, 'text/html');
          var node = doc.querySelector('[data-ian-finder-payload]');
          var data = readJson(node);
          if (!data) throw new Error('no payload');
          return data;
        })
        .catch(function (err) {
          delete cache[key];
          throw err;
        });
      return cache[key];
    }

    // Ian - a parameter listed in config.expand is OR'd across several values and is requested
    // per value with the payloads merged: facet counts and products_count summed, values
    // unioned. Off by default (see the header), kept as the escape hatch.
    function fetchCounts(list) {
      var groups = {};
      var fixed = [];
      list.forEach(function (p) {
        if (expand.indexOf(p.param.toLowerCase()) !== -1) {
          (groups[p.param] = groups[p.param] || []).push(p);
        } else {
          fixed.push(p);
        }
      });
      var keys = Object.keys(groups);
      if (!keys.length) return fetchPayload(list);

      var first = groups[keys[0]];
      var rest = [];
      keys.slice(1).forEach(function (k) { rest = rest.concat(groups[k]); });

      return Promise.all(first.map(function (p) {
        return fetchPayload(fixed.concat(rest, [p]));
      })).then(mergePayloads);
    }

    function mergePayloads(list) {
      if (list.length === 1) return list[0];
      var merged = { count: 0, filters: [], products: [] };
      var byParam = {};
      list.forEach(function (p) {
        merged.count += p.count || 0;
        merged.products = merged.products.concat(p.products || []);
        (p.filters || []).forEach(function (f) {
          var target = byParam[f.param];
          if (!target) {
            target = byParam[f.param] = { label: f.label, param: f.param, values: [] };
            merged.filters.push(target);
          }
          f.values.forEach(function (v) {
            var existing = null;
            for (var i = 0; i < target.values.length; i++) {
              if (target.values[i].value === v.value) { existing = target.values[i]; break; }
            }
            if (existing) {
              existing.count += v.count || 0;
            } else {
              target.values.push({ label: v.label, value: v.value, count: v.count || 0, url: v.url, swatch: v.swatch });
            }
          });
        });
      });
      return merged;
    }

    // ---- rendering ---------------------------------------------------------------------------

    var STEP_INDEX = { type: 0, group: 1, colour: 1, shape: 2, result: 3 };

    function setChips(step) {
      var idx = STEP_INDEX[step] || 0;
      var shapeChip = root.querySelector('[data-finder-step-shape]');
      var hasShape = !!config.shapeFilter;
      if (shapeChip) shapeChip.hidden = !hasShape;
      var visible = [];
      Array.prototype.forEach.call(el.steps, function (li) { if (!li.hidden) visible.push(li); });
      var logical = idx;
      if (!hasShape && idx >= 2) logical = idx - 1;
      visible.forEach(function (li, k) {
        if (k === logical) li.setAttribute('aria-current', 'step');
        else li.removeAttribute('aria-current');
      });
      if (el.progress) {
        var total = visible.length || 4;
        el.progress.style.width = Math.round(((logical + 1) / total) * 100) + '%';
      }
    }

    function shapeName(label) {
      return shapeLabels[(label || '').toLowerCase()] || label;
    }

    function summaryBits() {
      var bits = [];
      if (state.type) bits.push(state.type.label);
      if (state.colour) bits.push(state.colour);
      else if (state.group) bits.push(state.group.label);
      if (state.shape) bits.push(shapeName(state.shape));
      return bits;
    }

    function setCrumbs() {
      var bits = summaryBits();
      el.crumbs.innerHTML = '';
      if (!bits.length) { el.crumbs.innerHTML = '&nbsp;'; return; }
      bits.forEach(function (b, i) {
        if (i) el.crumbs.appendChild(document.createTextNode(' · '));
        var strong = document.createElement('b');
        strong.textContent = b;
        el.crumbs.appendChild(strong);
      });
    }

    function setHeading(num, question, hint) {
      el.qnum.textContent = num;
      el.question.textContent = question || '';
      el.hint.textContent = hint || '';
    }

    function setBar(opts) {
      el.bar.hidden = false;
      el.back.hidden = !opts.back;
      el.skip.hidden = !opts.skip;
      el.restart.hidden = !opts.restart;
    }

    function status(msg) {
      el.status.textContent = msg || '';
    }

    function busy(on) {
      if (on) root.setAttribute('data-finder-busy', '');
      else root.removeAttribute('data-finder-busy');
    }

    function clearTiles(columns) {
      screenToken++;
      el.tiles.innerHTML = '';
      el.tiles.classList.remove('ian-finder__screen');
      el.tiles.classList.toggle('ian-finder__tiles--3', columns === 3);
      // Ian - re-adding the class restarts the entry animation for the new screen.
      void el.tiles.offsetWidth;
      el.tiles.classList.add('ian-finder__screen');
      // Ian - scroll the question back into view inside the pop-up on small screens, where the
      // previous screen's tiles may have pushed it up.
      var scroller = root.closest('[data-finder-modal]');
      if (scroller && scroller.scrollTop > 0 && el.qnum) {
        var top = el.qnum.getBoundingClientRect().top + scroller.scrollTop - 24;
        if (top < scroller.scrollTop) scroller.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
      }
    }

    function media(src) {
      var box = document.createElement('span');
      box.className = 'ian-finder__tile-media';
      if (src) setImage(box, src, true);
      return box;
    }

    function setImage(box, src, immediate) {
      if (!box || !src || box.querySelector('img')) return;
      var img = document.createElement('img');
      img.alt = '';
      img.loading = 'lazy';
      img.decoding = 'async';
      if (immediate) img.className = 'ian-finder__img--static';
      img.addEventListener('load', function () { img.setAttribute('data-ready', ''); });
      img.src = src;
      box.appendChild(img);
      if (img.complete) img.setAttribute('data-ready', '');
    }

    function tile(opts) {
      var b = document.createElement('button');
      b.type = 'button';
      b.className = 'ian-finder__tile' + (opts.swatch ? ' ian-finder__tile--swatch' : '');
      var box = media(opts.image);
      b.appendChild(box);

      var body = document.createElement('span');
      body.className = 'ian-finder__tile-body';

      var label = document.createElement('span');
      label.className = 'ian-finder__tile-label';
      if (opts.swatch) {
        var dot = document.createElement('span');
        dot.className = 'ian-finder__swatch';
        dot.style.background = opts.swatch;
        label.appendChild(dot);
      }
      label.appendChild(document.createTextNode(opts.label));
      body.appendChild(label);

      var meta = document.createElement('span');
      meta.className = 'ian-finder__tile-meta';
      (opts.swatches || []).forEach(function (hex) {
        var d = document.createElement('span');
        d.className = 'ian-finder__swatch';
        d.style.background = hex;
        meta.appendChild(d);
      });
      var count = document.createElement('span');
      count.className = 'ian-finder__count';
      count.textContent = opts.note || pluralise(opts.count || 0);
      meta.appendChild(count);
      body.appendChild(meta);
      b.appendChild(body);

      b.disabled = !!opts.disabled;
      if (!opts.disabled) b.addEventListener('click', opts.onClick);
      el.tiles.appendChild(b);

      // Ian - no picked image: fetch a real product for this answer on idle and fade it in.
      // The screen token guards against painting into a screen that has since been replaced.
      if (!opts.image && opts.sampleParams && !opts.disabled) {
        var token = screenToken;
        schedule(function () {
          fetchPayload(opts.sampleParams).then(function (p) {
            if (token !== screenToken) return;
            var src = firstImage(p);
            if (src) setImage(box, src, false);
          }).catch(function () {});
        });
      }
      return b;
    }

    function swatchFor(value) {
      if (value && value.swatch) return value.swatch;
      var key = ((value && value.label) || '').toLowerCase();
      return FALLBACK_SWATCH[key] || '#cfcfcf';
    }

    // Colour groups with their members present in the collection's colour filter.
    function groupsFor(p) {
      var cf = colourFilter(p);
      return (config.colourGroups || []).map(function (g) {
        var members = [];
        var count = 0;
        splitList(g.colours).forEach(function (c) {
          var v = findValue(cf, c);
          if (v) { members.push(v); count += v.count || 0; }
        });
        return { group: g, members: members, count: count };
      });
    }

    // ---- screens -----------------------------------------------------------------------------

    function showType() {
      state.step = 'type';
      setChips('type');
      setCrumbs();
      setHeading(text.q1Label || 'Question 1', text.q1, text.q1Hint);
      setBar({ back: false, skip: false, restart: false });
      status('');
      clearTiles(4);
      var tf = typeFilter();
      (config.types || []).forEach(function (t) {
        var count = 0;
        splitList(t.types).forEach(function (name) {
          var v = findValue(tf, name);
          if (v) count += v.count || 0;
        });
        tile({
          label: t.label,
          count: count,
          image: t.image,
          sampleParams: typeParams(t),
          disabled: count === 0,
          onClick: function () { state.type = t; state.group = null; state.colour = null; state.shape = null; showGroups(); }
        });
      });
    }

    function load(list, then) {
      var token = ++requestToken;
      busy(true);
      status(text.loading || '');
      fetchCounts(list).then(function (p) {
        if (token !== requestToken) return;
        busy(false);
        status('');
        then(p);
      }).catch(function () {
        if (token !== requestToken) return;
        busy(false);
        // Ian - if the count request fails, fall back to the page's own unfiltered payload so the
        // quiz still completes; the result page is the collection filter, which is always right.
        status('');
        then(payload);
      });
    }

    function showGroups() {
      state.step = 'group';
      setChips('group');
      setCrumbs();
      setHeading('Question 2', text.q2, text.q2Hint);
      setBar({ back: true, skip: true, restart: true });
      clearTiles(4);
      load(params('type'), function (p) {
        clearTiles(4);
        groupsFor(p).forEach(function (g) {
          tile({
            label: g.group.label,
            count: g.count,
            image: g.group.image,
            swatches: g.members.map(swatchFor),
            sampleParams: typeParams(state.type).concat(colourParams(g.members.map(function (m) { return m.label; }))),
            disabled: g.count === 0,
            onClick: function () { state.group = g.group; state.colour = null; showExact(g, p); }
          });
        });
      });
    }

    function showExact(g, p) {
      var available = g.members.filter(function (v) { return (v.count || 0) > 0; });
      // Ian - one colour in the group means the exact question is a no-op: take it and move on.
      if (available.length <= 1) {
        state.colour = available.length ? available[0].label : null;
        showShape();
        return;
      }
      state.step = 'colour';
      setChips('colour');
      setCrumbs();
      setHeading('Question 2, optional', text.q2b, text.q2bHint);
      setBar({ back: true, skip: true, restart: true });
      clearTiles(available.length === 3 ? 3 : 4);
      available.forEach(function (v) {
        tile({
          label: v.label,
          swatch: swatchFor(v),
          note: pluralise(v.count || 0),
          sampleParams: typeParams(state.type).concat(colourParams([v.label])),
          onClick: function () { state.colour = v.label; showShape(); }
        });
      });
    }

    function showShape() {
      if (!config.shapeFilter) { showResult(); return; }
      state.step = 'shape';
      setChips('shape');
      setCrumbs();
      setHeading('Question 3', text.q3, text.q3Hint);
      setBar({ back: true, skip: true, restart: true });
      clearTiles(3);
      load(params('colour'), function (p) {
        var sf = shapeFilter(p);
        var options = sf ? sf.values.filter(function (v) { return (v.count || 0) > 0; }) : [];
        var total = typeof p.count === 'number' ? p.count : options.reduce(function (a, v) { return a + v.count; }, 0);
        // Ian - only ask when the answer would still split a page's worth of results.
        if (options.length < 2 || total <= (config.shapeMin || 0)) {
          state.shape = null;
          showResult(p);
          return;
        }
        clearTiles(options.length === 3 ? 3 : 4);
        options.forEach(function (v) {
          tile({
            label: shapeName(v.label),
            count: v.count || 0,
            sampleParams: params('colour').concat([{ param: sf.param, value: v.value }]),
            onClick: function () { state.shape = v.label; showResult(); }
          });
        });
      });
    }

    function showResult(known) {
      state.step = 'result';
      setChips('result');
      setCrumbs();
      setHeading('Your edit', text.result, text.resultHint);
      setBar({ back: true, skip: false, restart: true });
      clearTiles(4);
      var paint = function (p) {
        clearTiles(4);
        var count = typeof p.count === 'number' ? p.count : null;
        var box = document.createElement('div');
        box.className = 'ian-finder__result';

        var products = (p.products || []).filter(function (x) { return x && x.image; }).slice(0, resultProducts);
        if (products.length) {
          var grid = document.createElement('div');
          grid.className = 'ian-finder__result-products';
          products.forEach(function (x) {
            var a = document.createElement('a');
            a.className = 'ian-finder__result-product';
            a.href = x.url;
            a.appendChild(media(x.image));
            var body = document.createElement('span');
            body.className = 'ian-finder__tile-body';
            var title = document.createElement('span');
            title.className = 'ian-finder__result-title';
            title.textContent = x.title;
            body.appendChild(title);
            if (x.price) {
              var price = document.createElement('span');
              price.className = 'ian-finder__result-price';
              price.textContent = x.price;
              body.appendChild(price);
            }
            a.appendChild(body);
            grid.appendChild(a);
          });
          box.appendChild(grid);
        }

        var cta = document.createElement('div');
        cta.className = 'ian-finder__result-cta';
        var lead = document.createElement('div');
        var big = document.createElement('span');
        big.className = 'ian-finder__result-count';
        big.textContent = count !== null ? count : '';
        var sum = document.createElement('span');
        sum.className = 'ian-finder__result-sum';
        sum.textContent = (count !== null ? pluralise(count).replace(/^\d+\s/, '') + ' · ' : '') + summaryBits().join(' · ');
        lead.appendChild(big);
        lead.appendChild(sum);
        var link = document.createElement('a');
        link.className = 'ian-finder__btn ian-finder__btn--primary';
        link.href = resultUrl();
        link.textContent = (text.button || 'See all [count] colours').replace('[count]', count !== null ? count : '').replace(/\s{2,}/g, ' ').trim();
        cta.appendChild(lead);
        cta.appendChild(link);
        box.appendChild(cta);
        el.tiles.appendChild(box);
      };
      if (known && state.shape === null) { paint(known); return; }
      load(params(), paint);
    }

    // ---- controls ----------------------------------------------------------------------------

    el.back.addEventListener('click', function () {
      if (state.step === 'result') {
        if (state.shape) { state.shape = null; showShape(); return; }
        if (state.colour || state.group) { state.colour = null; showGroups(); return; }
        showType();
      } else if (state.step === 'shape') {
        state.shape = null;
        if (state.group) { state.colour = null; showGroups(); } else showType();
      } else if (state.step === 'colour') {
        state.colour = null; showGroups();
      } else {
        state.group = null; state.colour = null; showType();
      }
    });

    el.skip.addEventListener('click', function () {
      if (state.step === 'group') { state.group = null; state.colour = null; showShape(); }
      else if (state.step === 'colour') { state.colour = null; showShape(); }
      else if (state.step === 'shape') { state.shape = null; showResult(); }
    });

    el.restart.addEventListener('click', function () {
      state = { step: 'type', type: null, group: null, colour: null, shape: null };
      showType();
    });

    // ---- boot --------------------------------------------------------------------------------

    if (payload && payload.filters) {
      showType();
    } else {
      // Ian - the section sits on a page other than its collection, so the payload was empty.
      // Fetch it from the collection URL, rendering this section file by name.
      busy(true);
      status(text.loading || '');
      fetchPayload([]).then(function (p) {
        payload = p;
        busy(false);
        showType();
      }).catch(function () {
        busy(false);
        status('');
        // Ian - leave the server-rendered links in place; they still work as plain navigation.
      });
    }
  }

  // ---- pop-up --------------------------------------------------------------------------------

  function initModal(modal) {
    if (!modal || modal.hasAttribute('data-finder-ready')) return;
    modal.setAttribute('data-finder-ready', '');
    var hash = modal.getAttribute('data-finder-hash') || '#alpine-finder';
    var lastFocus = null;

    function open() {
      lastFocus = document.activeElement;
      // Ian - onto <body>, out of every ancestor stacking context.
      if (modal.parentNode !== document.body) document.body.appendChild(modal);
      modal.hidden = false;
      modal.scrollTop = 0;
      document.documentElement.classList.add('ian-finder-open');
      var first = modal.querySelector('.ian-finder__tile:not(:disabled), [data-finder-close]');
      if (first) first.focus({ preventScroll: true });
    }

    function close() {
      modal.hidden = true;
      document.documentElement.classList.remove('ian-finder-open');
      if (lastFocus && lastFocus.focus) lastFocus.focus();
    }

    // Ian - opens from the section's own button AND from any link on the page whose href ends
    // in the configured hash, which is how the collection nav's band and copy panel reach it.
    document.addEventListener('click', function (e) {
      var opener = e.target.closest && e.target.closest('[data-finder-open], a[href]');
      if (!opener) return;
      var isButton = opener.hasAttribute('data-finder-open');
      var href = opener.getAttribute('href') || '';
      if (!isButton && !(hash && href.slice(-hash.length) === hash)) return;
      e.preventDefault();
      open();
    });

    modal.addEventListener('click', function (e) {
      if (e.target.closest('[data-finder-close]')) { close(); return; }
      // Ian - a click on the dimmed backdrop itself closes; anything inside the panel does not.
      if (e.target === modal) close();
    });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && !modal.hidden) close();
    });
  }

  function boot() {
    Array.prototype.forEach.call(document.querySelectorAll('[data-ian-finder]'), init);
    Array.prototype.forEach.call(document.querySelectorAll('[data-finder-modal]'), initModal);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();

  // Ian - theme editor: re-init when the section is re-rendered after a setting change.
  document.addEventListener('shopify:section:load', boot);
})();
